import type { ICategory } from "./MultiCategoriesChipdownTypes";
import { CategorySelectionState } from "./MultiCategoriesChipdownTypes";

interface INormalizedCategory extends ICategory {
    sons: Array<ICategory & { selected: CategorySelectionState }>;
    selected: CategorySelectionState;
}

const resolveParentSelectionState = (parentCategory: ICategory): CategorySelectionState => {
    const sons = parentCategory.sons ?? [];
    if (sons.length === 0) {
        return parentCategory.selected ?? CategorySelectionState.EMPTY;
    }

    const selectedChildren = sons.filter(({ selected }) => selected === CategorySelectionState.FULL);

    if (selectedChildren.length === 0) {
        return CategorySelectionState.EMPTY;
    }

    return selectedChildren.length < sons.length ? CategorySelectionState.PARTIAL : CategorySelectionState.FULL;
};

export class CategoriesListManager {
    // @ts-ignore FIXME: strict migration - Property 'categories' has no initializer (TS2564) — assigned in constructor via init(), legacy class lifecycle
    private categories: INormalizedCategory[];
    private initialSelected: string[];
    private originalCategories: ICategory[];

    constructor(categories: ICategory[], initialSelected: string[]) {
        this.initialSelected = initialSelected;
        this.originalCategories = categories;
        this.init(categories);
    }

    private init = (parentCategories: ICategory[]) => {
        this.categories = parentCategories.map(this.initParentCategoriesSelection);
    };

    private initParentCategoriesSelection = (parentCategory: ICategory): INormalizedCategory => {
        const isParentSelected = this.initialSelected.includes(parentCategory.id);

        const sons =
            parentCategory.sons?.map((childCategory) => {
                // Resolve the child selection state. a child is considered selected in case
                // it appears on the initial selection list, or if its parent category is selected
                // (when a parent category is selected - all of its children should appear as selected)
                const isSelected = isParentSelected || this.initialSelected.includes(childCategory.id);

                return {
                    ...childCategory,
                    selected: isSelected ? CategorySelectionState.FULL : CategorySelectionState.EMPTY,
                    disabled: isParentSelected,
                };
            }) ?? [];

        return {
            ...parentCategory,
            sons,
            // In case the parent was selected, we know that all of its children were selected
            // otherwise - we need to check how many children were selected to determine the selection state
            // (Partial / Empty / Full)
            selected: isParentSelected
                ? CategorySelectionState.FULL
                : resolveParentSelectionState({ ...parentCategory, sons }),
        };
    };

    public reset = () => {
        this.init(this.originalCategories);
    };

    public clear = () => {
        this.categories.forEach((parentCat) => {
            parentCat.selected = CategorySelectionState.EMPTY;
            parentCat.sons.forEach((son) => {
                son.selected = CategorySelectionState.EMPTY;
            });
        });
    };

    public findParent = (childId: string) => {
        return this.categories.find((parent) => typeof parent.sons.find((son) => son.id === childId) !== "undefined");
    };

    public getCategories = () => {
        return this.categories;
    };

    public toggleParentCategory = (parentCategoryId: string) => {
        const parentCategory = this.categories.find(({ id }) => id === parentCategoryId);
        if (parentCategory) {
            switch (parentCategory.selected) {
                case CategorySelectionState.FULL:
                    parentCategory.selected = CategorySelectionState.EMPTY;
                    parentCategory.sons.forEach((son) => {
                        son.selected = CategorySelectionState.EMPTY;
                    });
                    break;
                case CategorySelectionState.EMPTY:
                case CategorySelectionState.PARTIAL:
                default:
                    parentCategory.sons?.forEach((son) => {
                        son.selected = CategorySelectionState.FULL;
                        // this value is to allow disabling children when their parent is selected
                        son.disabled = true;
                    });
                    parentCategory.selected = CategorySelectionState.FULL;
                    break;
            }
        }
        return this.categories;
    };

    public toggleSubCategory = (subCategoryId: string) => {
        const parentCategory = this.findParent(subCategoryId);
        if (parentCategory) {
            const subCategory = parentCategory.sons.find(({ id }) => id === subCategoryId);
            // @ts-ignore FIXME: strict migration - 'subCategory' possibly undefined (TS18048) — findParent matched this id among the parent's sons, so it exists
            subCategory.selected = subCategory.selected ? CategorySelectionState.EMPTY : CategorySelectionState.FULL;
            parentCategory.selected = resolveParentSelectionState(parentCategory);
        }
        return this.categories;
    };

    public getSelectedCategories = (
        onlyFull: boolean = false,
        ignoreParents: boolean = false,
    ): [ICategory[], number] => {
        // This counter tracks only non-children and children who's parent is not selected
        let netSelectedCategoriesCount = 0;
        const targetState = onlyFull ? CategorySelectionState.FULL : CategorySelectionState.PARTIAL;
        const selectedCategories = this.categories.reduce<ICategory[]>((result, current) => {
            if ((!ignoreParents || current.sons.length === 0) && current.selected >= targetState) {
                result.push(current);
                if (current.selected === CategorySelectionState.FULL) {
                    netSelectedCategoriesCount++;
                }
            }
            current.sons.forEach((son) => {
                if (son.selected >= targetState) {
                    result.push(son);
                }
                if (current.selected !== CategorySelectionState.FULL && son.selected === CategorySelectionState.FULL) {
                    netSelectedCategoriesCount++;
                }
            });
            return result;
        }, []);
        return [selectedCategories, netSelectedCategoriesCount];
    };
}
